import { Controller } from "@hotwired/stimulus";

/**
 * Le chrono d'une épreuve chronométrée — carte #1608, lot J.
 *
 * ⚠️ Ex-`weekly-quiz` : les Olympiades rendent le MÊME écran d'épreuve (prototype,
 * `goWebExam` → `webRoute: 'exam'`). Seul le nom a changé.
 *
 * 🔴 **Le client n'est JAMAIS autorité sur le temps restant.** Décision d'API n° 8 du Handoff :
 * « il lit une horloge serveur ». Ce contrôleur ne lit donc jamais `Date.now()` comme référence :
 * il calcule une fois, au chargement, l'écart entre l'horloge du navigateur et celle du serveur,
 * puis affiche `serveur = navigateur - écart`.
 *
 * ⚠️ **Ce n'est pas une précaution théorique** : le point de QA le chiffre — « vérifier le
 * comportement quand l'horloge du téléphone est fausse de 20 minutes ». Sans la correction,
 * l'élève verrait vingt minutes de trop ou de moins, et croirait que le produit lui vole du
 * temps.
 *
 * 🔴 **Le chrono AFFICHE, il n'arbitre pas.** Le serveur refuse une réponse hors fenêtre, quoi
 * qu'affiche cet écran. Faire décider le client rendrait le chrono contournable en changeant
 * l'heure du téléphone.
 *
 * ⚠️ **Sans dramatisation** — *Cycles* écran D : « un filet de 4 px qui se vide, et un nombre en
 * mono. Pas de rouge, pas de clignotement, pas de compte à rebours plein écran. Le filet passe à
 * l'ambre dans les 10 derniers pourcents — un changement de teinte, pas une alarme. »
 */
export default class extends Controller {
  static targets = ["countdown", "clock", "bar"];
  static values = { serverNow: String, startsAt: String, endsAt: String };

  connect() {
    if (!this.hasServerNowValue) return;
    // L'écart mesuré UNE fois : tout le reste s'en déduit.
    this.skew = Date.now() - Date.parse(this.serverNowValue);
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnect() {
    if (this.timer) clearInterval(this.timer);
  }

  /** L'instant serveur, dérivé de l'horloge locale corrigée de l'écart. */
  serverTime() {
    return Date.now() - this.skew;
  }

  tick() {
    const now = this.serverTime();
    if (this.hasCountdownTarget && this.hasStartsAtValue) {
      this.renderCountdown(Date.parse(this.startsAtValue) - now);
    }
    if (this.hasClockTarget && this.hasEndsAtValue) {
      this.renderClock(Date.parse(this.endsAtValue) - now);
    }
  }

  renderCountdown(remainingMs) {
    this.countdownTarget.textContent =
      remainingMs > 0 ? this.format(remainingMs) : "";
  }

  renderClock(remainingMs) {
    const remaining = Math.max(remainingMs, 0);
    this.clockTarget.textContent = this.format(remaining);
    if (!this.hasBarTarget || !this.hasStartsAtValue) return;

    const total = Date.parse(this.endsAtValue) - Date.parse(this.startsAtValue);
    const share = total > 0 ? remaining / total : 0;
    this.barTarget.style.width = `${Math.round(share * 100)}%`;
    // ⚠️ Une TEINTE, jamais une alarme : le filet passe à l'ambre dans les 10 derniers pourcents.
    this.barTarget.classList.toggle("is-ending", share <= 0.1);
  }

  format(ms) {
    const total = Math.max(Math.floor(ms / 1000), 0);
    const minutes = String(Math.floor(total / 60)).padStart(2, "0");
    const seconds = String(total % 60).padStart(2, "0");
    return `${minutes}:${seconds}`;
  }
}
